import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SettingsAddress } from 'icreate/common/models/alarm-group';
import { SensorTagSlot } from 'icreate/common/models/sensor-cable-tag';
import { emptySlot, emptyValue, getSlotPinNum } from 'icreate/common/models/slot';
import { fromEvent, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-address-selectors',
    templateUrl: './address-selectors.component.html',
    styleUrl: './address-selectors.component.css',
    standalone: false,
})
export class AddressSelectorsComponent implements OnInit, OnDestroy {
    readonly fcuSelector = 'fcu';
    readonly fuSelector = 'fu';
    readonly slotSelector = 'slot';
    readonly terminalSelector = 'terminal';
    readonly NO_SELECTION = 'no-slection';

    @Input() vesselSlots: SensorTagSlot[];
    @Input() settingAddress: SettingsAddress;
    @Input() hasRowPadding: boolean = true;
    @Output() onSelectorChange: EventEmitter<SettingsAddress> = new EventEmitter();
    @ViewChild('fcuSelElement') fcuSelElement: ElementRef;
    @ViewChild('fuSelElement') fuSelElement: ElementRef;
    @ViewChild('slotSelElement') slotSelElement: ElementRef;
    @ViewChild('terminalSelElement') terminalSelElement: ElementRef;
    @ViewChild('selectorElement') selectorElement: ElementRef;

    isFcuSelectorOpen: boolean = false;
    selectedFcuNum: number;
    isFuSelectorOpen: boolean = false;
    selectedFuNum: number;
    isSlotSelectorOpen: boolean = false;
    selectedSlotNum: number;
    isTerminalSelectorOpen: boolean = false;
    selectedTerminal: number;

    optionsTop: string;
    optionsLeft: string;

    private destroy$: Subject<void> = new Subject();

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent) {
        const clickedOutsideSelector = !this.selectorElement?.nativeElement.contains(event.target);
        if (clickedOutsideSelector) {
            this.isFcuSelectorOpen = false;
            this.isFuSelectorOpen = false;
            this.isSlotSelectorOpen = false;
            this.isTerminalSelectorOpen = false;
        }
    }

    constructor(
        private cdr: ChangeDetectorRef,
        private translate: TranslateService
    ) {}

    ngOnInit(): void {
        if (this.settingAddress) {
            this.selectedFcuNum = this.settingAddress.fcuNum;
            this.selectedFuNum = this.settingAddress.fuNum;
            this.selectedSlotNum = this.settingAddress.slotNum;
            this.selectedTerminal = this.settingAddress.terminal;
        }
        const scrollContainer = document.querySelector('.content-group-setttings-dialog');
        if (scrollContainer) {
            fromEvent(scrollContainer, 'scroll')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.isFcuSelectorOpen = false;
                    this.isFuSelectorOpen = false;
                    this.isSlotSelectorOpen = false;
                    this.isTerminalSelectorOpen = false;
                });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    openFcuSelector(event) {
        this.isFuSelectorOpen = false;
        this.isSlotSelectorOpen = false;
        this.isTerminalSelectorOpen = false;
        if (this.isFcuSelectorOpen) {
            this.updatePosition();
            this.isFcuSelectorOpen = false;
        } else {
            this.updatePosition(this.fcuSelElement);
            this.isFcuSelectorOpen = true;
        }
        event.stopPropagation();
    }

    openFuSelector(event) {
        this.isFcuSelectorOpen = false;
        this.isSlotSelectorOpen = false;
        this.isTerminalSelectorOpen = false;
        if (this.isFuSelectorOpen) {
            this.updatePosition();
            this.isFuSelectorOpen = false;
        } else {
            this.updatePosition(this.fuSelElement);
            this.isFuSelectorOpen = true;
        }
        event.stopPropagation();
    }

    openSlotSelector(event) {
        if (this.getSelectorOptions(this.slotSelector).length === 0) return;
        this.isFcuSelectorOpen = false;
        this.isFuSelectorOpen = false;
        this.isTerminalSelectorOpen = false;
        if (this.isSlotSelectorOpen) {
            this.updatePosition();
            this.isSlotSelectorOpen = false;
        } else {
            this.updatePosition(this.slotSelElement);
            this.isSlotSelectorOpen = true;
        }
        event.stopPropagation();
    }

    openTerminalSelector(event) {
        if (!this.getSlotTerminals()) return;
        this.isFcuSelectorOpen = false;
        this.isFuSelectorOpen = false;
        this.isSlotSelectorOpen = false;
        if (this.isTerminalSelectorOpen) {
            this.updatePosition();
            this.isTerminalSelectorOpen = false;
        } else {
            this.updatePosition(this.terminalSelElement);
            this.isTerminalSelectorOpen = true;
        }
        event.stopPropagation();
    }

    getSelectorOptions(selector) {
        if (!this.vesselSlots?.length) return [];

        let key;
        if (selector == this.fcuSelector) key = 'fcuNum';
        if (selector == this.fuSelector) key = 'fuNum';
        if (selector == this.slotSelector) key = 'no';
        if (selector == this.terminalSelector) key = 'terminal';
        if (!key) return [];

        if (selector == this.terminalSelector) {
            const slotTerminals = this.getSlotTerminals();
            if (!slotTerminals) return;
            return Array.from({ length: slotTerminals }, (v, i) => i + 1);
        }

        const filters = {};
        if (this.selectedFcuNum) filters['fcuNum'] = this.selectedFcuNum;
        if (this.selectedFuNum && key !== 'fcuNum') {
            if (this.selectedFuNum == null) filters['fuNum'] = undefined;
            else filters['fuNum'] = this.selectedFuNum;
        }

        if (selector === this.fcuSelector) delete filters['fcuNum'];
        if (selector === this.fuSelector) delete filters['fuNum'];

        const options = this.vesselSlots
            .filter(slot => {
                if (selector !== this.fcuSelector && (slot.ioType === emptySlot || slot.ioType == emptyValue))
                    return false;
                return Object.entries(filters).every(([k, val]) => slot[k] === val);
            })
            .map(slot => slot[key])
            .filter(Boolean)
            .sort((a, b) => a - b);

        if (selector == this.fuSelector) {
            options.unshift(this.NO_SELECTION);
        }
        return [...new Set(options)];
    }

    async optionSelected(selector, option) {
        if (selector == this.fcuSelector) {
            if (this.selectedFcuNum === option) return;
            this.selectedFcuNum = option;
            this.selectedFuNum = undefined;
            this.selectedSlotNum = undefined;
            this.selectedTerminal = null;
        } else if (selector == this.fuSelector) {
            const fuNum = option == this.NO_SELECTION ? null : option;
            if (this.selectedFuNum === fuNum) return;
            this.selectedFuNum = fuNum;
            this.selectedSlotNum = undefined;
            this.selectedTerminal = null;
        } else if (selector == this.slotSelector) {
            if (this.selectedSlotNum === option) return;
            this.selectedSlotNum = option;
            this.selectedTerminal = null;
        } else if (selector == this.terminalSelector) {
            if (this.selectedTerminal === option) return;
            this.selectedTerminal = option;
        }

        const slotTerminals = this.getSlotTerminals();
        if (!slotTerminals) this.selectedTerminal = null;
        this.sendUpdatedAddresses();
        this.cdr.detectChanges();
    }

    getSlotTerminals(): number {
        if (isNaN(this.selectedFcuNum) || isNaN(this.selectedSlotNum)) return;
        const slot = this.vesselSlots.find(
            slot =>
                this.selectedFcuNum === slot.fcuNum &&
                (!this.selectedFuNum || this.selectedFuNum === slot.fuNum) &&
                this.selectedSlotNum === slot.no
        );

        if (!slot) return;
        return getSlotPinNum(slot.ioType, slot.terminal);
    }

    formatFcu(option: number): string {
        return this.translate.instant('sensor-cable-tag.selected.fcu', { num: option });
    }

    formatFu(option): string {
        return option == this.NO_SELECTION
            ? this.getNoSelectionText()
            : this.translate.instant('sensor-cable-tag.selected.fu', { num: option });
    }

    getNoSelectionText() {
        return this.translate.instant('sensor-cable-tag.filter.no-selection');
    }

    paddNumber(number: number) {
        return number.toString().padStart(2, '0');
    }

    sendUpdatedAddresses() {
        this.onSelectorChange.emit({
            fcuNum: this.selectedFcuNum,
            fuNum: this.selectedFuNum,
            slotNum: this.selectedSlotNum,
            terminal: this.selectedTerminal,
        });
    }

    private updatePosition(elementRef?) {
        if (!elementRef) {
            this.optionsTop = null;
            this.optionsLeft = null;
            return;
        }
        const selectorPosition = elementRef.nativeElement.getBoundingClientRect();

        const scrollTop = window.scrollY;
        const scrollLeft = window.scrollX;

        let top = selectorPosition.bottom + scrollTop;

        this.optionsTop = top + 1 + 'px';
        this.optionsLeft = selectorPosition.left + scrollLeft + 'px';

        this.cdr.detectChanges();
    }
}
